import { isVideoCall } from 'Modules/softphone/guards';
import type { InboundCallInfo } from 'Modules/softphone/services/CallManager/index.types';
import { createLogger } from 'Modules/softphone/utils/logger';
import type { Invitation } from 'sip.js';
import { isPhoneNumber } from 'Utils/phoneUtil';
import { searchPersonBySip } from 'Utils/searchPersonBySip';

const logger = createLogger('CallManager:Parsers');

/**
 * Extract call information from SIP INVITE
 */
export async function extractCallInfo(
  invitation: Invitation
): Promise<InboundCallInfo> {
  const callId = invitation.id;

  const fromHeader = invitation.request.from;
  let displayName = fromHeader?.displayName || undefined;

  displayName = stripRingGroupPrependPrefix(displayName);

  if (displayName && displayName.includes('+')) {
    displayName = displayName.split('+')[0].trim();
  }

  let phoneNumber = '';
  let personId: string | undefined;

  if (invitation.remoteIdentity?.uri) {
    phoneNumber = invitation.remoteIdentity.uri.user || '';
  }

  if (!phoneNumber) {
    const fromUri = fromHeader?.uri?.toString() || '';
    phoneNumber = extractPhoneNumber(fromUri);
  }

  const isExtension = phoneNumber && !isPhoneNumber(phoneNumber);

  if (isExtension && invitation.remoteIdentity?.uri) {
    const user = invitation.remoteIdentity.uri.user;
    const host = invitation.remoteIdentity.uri.host;
    const sipUri = `${user}@${host}`;

    logger.debug('Extension detected, searching for person by SIP', {
      extension: phoneNumber,
      sipUri,
    });

    try {
      const person = await searchPersonBySip(sipUri);

      if (person) {
        personId = person.sourceId?.toString();
        const personPhoneNumber = person.phoneNumber;

        if (personPhoneNumber) {
          logger.debug('Found person with phone number', {
            extension: phoneNumber,
            personId,
            phoneNumber: personPhoneNumber,
          });
          phoneNumber = personPhoneNumber;
        }
      }
    } catch (error) {
      logger.debug('Failed to search person by SIP', {
        sipUri,
        error: error instanceof Error ? error.message : String(error),
      });
    }
  }

  const isVideo = isVideoCall(invitation);

  const ringGroupName = extractRingGroupName(invitation);

  logger.debug('Extracted call info', {
    callId,
    phoneNumber,
    displayName,
    isVideo,
    ringGroupName,
  });

  return {
    callId,
    invitation,
    phoneNumber,
    displayName,
    ringGroupName,
    isVideo,
    personId,
  };
}

/**
 * Extract phone number from SIP URI
 */
export function extractPhoneNumber(uri: string): string {
  const match = uri.match(/sip:([^@]+)@/);
  return match ? match[1] : uri;
}

/**
 * Split a "<prefix>: <rest>" string on its first colon, trimming both sides.
 * Returns undefined if there's no colon or either side is empty once trimmed
 * - the single source of truth for parsing the PBX's prepend caller ID
 * format, shared by stripRingGroupPrependPrefix() and
 * resolveRingGroupCandidate().
 */
function splitPrependPrefix(
  name: string
): { prefix: string; rest: string } | undefined {
  const colonIndex = name.indexOf(':');
  if (colonIndex === -1) {
    return undefined;
  }

  const prefix = name.slice(0, colonIndex).trim();
  const rest = name.slice(colonIndex + 1).trim();

  return prefix && rest ? { prefix, rest } : undefined;
}

/**
 * When a ring group has "prepend caller ID" enabled, the PBX formats the
 * caller's display name as "<ring group name>: <contact name>" - split that
 * off and return just the contact name. Returns the input unchanged if it
 * doesn't match that pattern.
 */
function stripRingGroupPrependPrefix(
  name: string | undefined
): string | undefined {
  if (!name) {
    return name;
  }

  return splitPrependPrefix(name)?.rest ?? name;
}

/**
 * Extract ring group name from a SIP INVITE.
 *
 * Checks dedicated headers first (most authoritative), then falls back to
 * a "prepend caller ID" prefix on the From display name when some PBXs only
 * rewrite From (e.g. `"Ring Group: Contact Name"`) and omit the headers below.
 *
 * Priority:
 * 1. X-Ring-Group (custom header with direct value)
 * 2. Diversion (standard header with display name)
 * 3. Remote-Party-ID (standard header with display name)
 * 4. P-Asserted-Identity (standard header with display name)
 * 5. From display name prepend (`"<ring group>: <rest>"`)
 */
export function extractRingGroupName(
  invitation: Invitation
): string | undefined {
  // Priority order for header checking
  const headers = [
    'X-Ring-Group',
    'Diversion',
    'Remote-Party-ID',
    'P-Asserted-Identity',
  ];

  // Remote-Party-ID/P-Asserted-Identity are sent by most PBXs on nearly every
  // call (not just ring-group ones). When the ring group has "prepend caller
  // ID" enabled, the PBX formats these as "<ring group name>: <contact
  // name>" - that's the real ring group signal, so split it out. Without
  // that prefix, these headers typically just echo the caller's own SIP
  // From display name rather than carrying a real ring group name; guard
  // against that case using the raw SIP data (a contact-resolved name may be
  // formatted differently downstream and miss the match).
  const rawFromDisplayName = invitation.request.from?.displayName;
  const fromDisplayName = rawFromDisplayName?.trim().toLowerCase();

  for (const headerName of headers) {
    const candidate = resolveRingGroupCandidate(
      headerName,
      invitation.request.getHeader(headerName),
      fromDisplayName
    );

    if (candidate) {
      logger.debug('Ring group extracted', { headerName, name: candidate });
      return candidate;
    }
  }

  // Fallback: some PBXs (e.g. FreeSWITCH) only put the ring group into the
  // From display name as "<ring group>: <contact/location>" with no dedicated
  // ring-group headers. Prefer dedicated headers above when present.
  const fromPrepend = rawFromDisplayName
    ? splitPrependPrefix(rawFromDisplayName)
    : undefined;

  if (fromPrepend) {
    logger.debug('Ring group extracted from From display name', {
      name: fromPrepend.prefix,
    });
    return fromPrepend.prefix;
  }

  logger.debug('No ring group found in SIP headers');
  return undefined;
}

/**
 * Resolve a single header's ring group candidate, applying the
 * prepend-caller-ID split and echo guard for Remote-Party-ID/P-Asserted-Identity.
 * Returns undefined when the header has no usable value, doesn't parse, or
 * turns out to just echo the caller's own display name.
 */
function resolveRingGroupCandidate(
  headerName: string,
  headerValue: string | undefined,
  fromDisplayName: string | undefined
): string | undefined {
  if (!headerValue) {
    return undefined;
  }

  const name = parseRingGroupFromHeader(headerValue, headerName);
  if (!name) {
    return undefined;
  }

  const isEchoProneHeader =
    headerName === 'Remote-Party-ID' || headerName === 'P-Asserted-Identity';
  if (!isEchoProneHeader) {
    return name;
  }

  const split = splitPrependPrefix(name);
  if (split) {
    logger.debug('Ring group extracted from prepend caller ID format', {
      headerName,
      prependName: split.prefix,
    });
    return split.prefix;
  }

  if (name.includes(':')) {
    return name;
  }

  const echoesCallerDisplayName =
    fromDisplayName !== undefined &&
    name.trim().toLowerCase() === fromDisplayName;

  if (echoesCallerDisplayName) {
    logger.debug('Ignoring ring group candidate - echoes caller display name', {
      headerName,
      name,
    });
    return undefined;
  }

  return name;
}

/**
 * Parse ring group name from a specific SIP header
 * Different headers have different formats for encoding the ring group name
 *
 * @param headerValue - The raw header value from the SIP message
 * @param headerName - The name of the header being parsed
 * @returns The extracted ring group name, or undefined if parsing fails
 */
function parseRingGroupFromHeader(
  headerValue: string,
  headerName: string
): string | undefined {
  if (!headerValue || typeof headerValue !== 'string') {
    return undefined;
  }

  const trimmedValue = headerValue.trim();
  if (!trimmedValue) {
    return undefined;
  }

  try {
    switch (headerName) {
      case 'X-Ring-Group':
        // Direct value: "Sales Team"
        return trimmedValue;

      case 'Diversion':
        // Format: <sip:ringgroup@domain>;reason=unconditional
        // Or: "Ring Group Name" <sip:ringgroup@domain>;reason=unconditional
        // Extract display name if present in quotes
        {
          const diversionMatch = trimmedValue.match(/"([^"]+)"/);
          if (diversionMatch) {
            return diversionMatch[1];
          }
          // If no quoted display name, return undefined
          return undefined;
        }

      case 'Remote-Party-ID':
      case 'P-Asserted-Identity':
        // Format: "Ring Group Name" <sip:number@domain>
        // Or: <sip:number@domain>
        // Extract display name from quotes
        {
          const match = trimmedValue.match(/"([^"]+)"/);
          if (match) {
            return match[1];
          }
          // If no quoted display name, return undefined
          return undefined;
        }

      default:
        logger.debug('Unknown header type for ring group parsing', {
          headerName,
        });
        return undefined;
    }
  } catch (error) {
    logger.debug('Error parsing ring group from header', {
      headerName,
      headerValue: trimmedValue,
      error: error instanceof Error ? error.message : String(error),
    });
    return undefined;
  }
}
